import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ClockIcon,
  EyeIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import {
  PRACTICE_LABELS,
  accuracyPercent,
  attemptTimestamp,
  buildPracticeHistoryPath,
  normalizeAttempt,
} from '../lib/practiceAttempts';
import { formatIeltsBand, ieltsBandFromAttempt } from '../lib/ieltsSpeaking';
import PracticeResultAdapter from './PracticeResultAdapters';

const formatDate = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale === 'zh-CN' ? 'zh-CN' : 'en', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatDuration = (value) => {
  const milliseconds = Number(value || 0);
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return '';
  const seconds = Math.round(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
};

export default function UnifiedPracticeResult({
  detail,
  viewer = 'student',
  onPracticeAgain,
  historyPath,
  backPath,
  starting = false,
}) {
  const { locale, t } = useI18n();
  const attempt = normalizeAttempt(detail?.attempt || {});
  const assignment = detail?.assignment || attempt.assignment || {};
  const practiceType = assignment.practice_type || attempt.practice_type || detail?.practice_type;
  const resolvedHistoryPath = historyPath || buildPracticeHistoryPath(assignment.id || attempt.assignment_id, viewer);
  const resolvedBackPath = backPath || resolvedHistoryPath;
  const accuracy = accuracyPercent(attempt.accuracy);
  const isIelts = practiceType === 'ielts_speaking';
  const predictedBand = isIelts ? ieltsBandFromAttempt(attempt) : null;
  const status = attempt.status === 'completed' ? t('Completed') : t('Submitted');
  const attemptLabel = attempt.attempt_no === 1
    ? t('First attempt')
    : t('Attempt {number}', { number: attempt.attempt_no || '?' });

  return (
    <main className="mx-auto max-w-5xl p-4 md:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-4">
        <Link to={resolvedBackPath} className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 hover:underline">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          {t('Back to attempts')}
        </Link>
        {viewer === 'student' && (
          <div className="flex flex-wrap items-center gap-2">
            <Link to={resolvedHistoryPath} className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50">
              <EyeIcon className="h-4 w-4" aria-hidden="true" />
              {t('View all attempts')}
            </Link>
            <button
              type="button"
              onClick={onPracticeAgain}
              disabled={starting || !assignment.id}
              className="inline-flex items-center gap-2 rounded bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-50"
            >
              {starting ? <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" /> : <PlayIcon className="h-4 w-4" aria-hidden="true" />}
              {t('Practice again')}
            </button>
          </div>
        )}
      </div>

      <header className="py-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
              {t(PRACTICE_LABELS[practiceType] || 'Practice')}
            </div>
            <h1 className="mt-1 text-2xl font-bold text-gray-950 sm:text-3xl" data-i18n-skip>
              {assignment.title || t('Practice result')}
            </h1>
          </div>
          <span className={`inline-flex w-fit items-center gap-2 rounded border px-3 py-1.5 text-sm font-semibold ${attempt.status === 'completed' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
            {status}
          </span>
        </div>

        <div className="mt-5 grid grid-cols-2 border border-gray-200 bg-white sm:grid-cols-4">
          <div className="border-b border-gray-200 p-3 sm:border-b-0 sm:border-r">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('Attempt')}</div>
            <div className="mt-1 text-sm font-bold text-gray-900">{attemptLabel}</div>
          </div>
          <div className="border-b border-gray-200 p-3 sm:border-b-0 sm:border-r">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t(isIelts ? 'Predicted band' : 'Accuracy')}</div>
            <div className="mt-1 text-lg font-bold text-gray-900" data-i18n-skip>{isIelts ? (predictedBand == null ? t('Not available') : formatIeltsBand(predictedBand)) : accuracy == null ? t('Not available') : `${accuracy}%`}</div>
          </div>
          <div className="border-r border-gray-200 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('Time used')}</div>
            <div className="mt-1 flex items-center gap-2 text-sm font-bold text-gray-900"><ClockIcon className="h-4 w-4" aria-hidden="true" />{formatDuration(attempt.duration_ms) || t('Not available')}</div>
          </div>
          <div className="p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('Completed at')}</div>
            <div className="mt-1 text-sm font-bold text-gray-900" data-i18n-skip>{formatDate(attemptTimestamp(attempt), locale) || t('Not available')}</div>
          </div>
        </div>
      </header>

      <section className="border-t border-gray-200 pt-6">
        <PracticeResultAdapter practiceType={practiceType} attempt={attempt} details={detail?.details || {}} t={t} viewer={viewer} />
      </section>
    </main>
  );
}
